<script lang="ts" setup>

type NavLink = {
  to: string
  label: string
  children: NavLink[]
}


import { AuthUser } from '@/types/AuthUser';
import { ref, computed, watch, onBeforeUnmount  } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/useAuthStore'
import IcelandicFlag from '@/assets/icons/icelandic_flag.svg'
import ENFlag from '@/assets/icons/EN.svg'
import { useAssetPath } from '@/composables/useAssetPath'

const logoSrc = useAssetPath('gildi.svg')

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()
const authStore = useAuthStore()

const showMobileMenu = ref(false)
const isProfileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
  if (showMobileMenu.value) {
    isProfileMenuOpen.value = false
  }
}

const toggleProfileMenu = () => {
  isProfileMenuOpen.value = !isProfileMenuOpen.value
  if (isProfileMenuOpen.value) {
    showMobileMenu.value = false
  }
}

function logout() {
  authStore.logOut()
  isProfileMenuOpen.value = false
}

function changeLang(lang: string) {
  locale.value = lang
  isProfileMenuOpen.value = false
}

const menuStack = ref<NavLink[]>([])
const currentMenu = computed<NavLink[]>(() =>
  menuStack.value.length === 0
    ? navLinks.value
    : menuStack.value[menuStack.value.length - 1].children
)

const drillInto = (link: NavLink) => {
  menuStack.value.push(link)
}

const drillBack = () => {
  menuStack.value.pop()
}

onBeforeUnmount(() => {
  document.body.classList.remove('body--locked')
})

watch(showMobileMenu, (val) => {
  document.body.classList.toggle('body--locked', val)
    if (!val) menuStack.value = []
})

watch(isProfileMenuOpen, (val) => {
  document.body.classList.toggle('body--locked', val)
})

watch(() => route.fullPath, () => {
  showMobileMenu.value = false
  isProfileMenuOpen.value = false
})



const navLinks = computed(() => {
  const seen = new Set<string>()

  const links = router
    .getRoutes()
    .filter(r => {
      if (r.path === '/' || r.path === '/stillingar' || r.path === '/impersonate') return false
      const isTopLevel = r.path.split('/').filter(Boolean).length === 1
      return r.meta?.breadcrumb && isTopLevel
    })
    .filter(r => {
      const key = r.meta?.breadcrumb as string
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .map(r => {
      const children: NavLink[] = r.children
        ?.filter(c => c.meta?.breadcrumb)
        .map(c => ({
          to: r.path.replace(/\/$/, '') + '/' + c.path,
          label: t(String(c.meta?.breadcrumb || c.meta?.titleKey || '')),
          children: []
        })) || []

      return {
        to: r.path,
        label: t(String(r.meta?.breadcrumb || r.meta?.titleKey || '')),
        children
      }
    })

  return links as NavLink[]
})
</script>


<template>
    <header class="mobile-header">
      <div class="mobile-header__top">
        <!-- Logo -->
        <router-link
            to="/"
            class="mobile-header__logo"
            >
            <img
              :src="logoSrc"
              alt="Gildi logo"
              >
        </router-link>
  
        <!-- Icons -->
        <div class="mobile-header__actions">
          <button aria-label="Profile" @click="toggleProfileMenu">
            <svg
                width="40"
                height="41"
                viewBox="0 0 40 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                >
                <rect
                y="0.5"
                width="40"
                height="40"
                rx="4"
                class="icon-bg"
                />
                <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M16.5277 19.8632C17.5555 20.55 18.7639 20.9165 20 20.9165C21.6576 20.9165 23.2473 20.258 24.4194 19.0859C25.5915 17.9138 26.25 16.3241 26.25 14.6665C26.25 13.4304 25.8834 12.222 25.1967 11.1942C24.5099 10.1664 23.5338 9.36531 22.3918 8.89226C21.2497 8.41921 19.9931 8.29544 18.7807 8.5366C17.5683 8.77776 16.4547 9.37301 15.5806 10.2471C14.7065 11.1212 14.1113 12.2348 13.8701 13.4472C13.6289 14.6596 13.7527 15.9162 14.2258 17.0583C14.6988 18.2003 15.4999 19.1764 16.5277 19.8632ZM17.9166 11.5485C18.5333 11.1364 19.2583 10.9165 20 10.9165C20.9946 10.9165 21.9484 11.3116 22.6517 12.0149C23.3549 12.7181 23.75 13.6719 23.75 14.6665C23.75 15.4082 23.5301 16.1332 23.118 16.7499C22.706 17.3666 22.1203 17.8472 21.4351 18.1311C20.7498 18.4149 19.9958 18.4891 19.2684 18.3445C18.541 18.1998 17.8728 17.8426 17.3484 17.3182C16.8239 16.7937 16.4668 16.1255 16.3221 15.3981C16.1774 14.6707 16.2516 13.9167 16.5355 13.2314C16.8193 12.5462 17.2999 11.9606 17.9166 11.5485ZM30.7876 32.2123C31.0211 32.4458 31.3365 32.5789 31.6667 32.5832C31.9968 32.5789 32.3123 32.4458 32.5458 32.2123C32.7793 31.9788 32.9124 31.6633 32.9167 31.3332C32.9167 23.4165 23.8667 23.4165 20 23.4165C16.1333 23.4165 7.08334 23.4165 7.08334 31.3332C7.08334 31.6647 7.21503 31.9826 7.44945 32.2171C7.68387 32.4515 8.00182 32.5832 8.33334 32.5832C8.66486 32.5832 8.9828 32.4515 9.21722 32.2171C9.45164 31.9826 9.58334 31.6647 9.58334 31.3332C9.58334 28.0832 11.35 25.9165 20 25.9165C28.65 25.9165 30.4167 28.0832 30.4167 31.3332C30.421 31.6633 30.5541 31.9788 30.7876 32.2123Z"
                class="icon-path"
                />
            </svg>
          </button>
          <button
            aria-label="Toggle menu"
            @click="toggleMobileMenu"
            >
            <svg class="menu-icon" viewBox="0 0 40 41" width="40" height="40">
                <rect y="0.5" width="40" height="40" rx="4" class="icon-bg" />
                 <path
                    :class="{ bar: true, top: true, open: showMobileMenu }"
                    d="M12 14H28"
                    class="menu-bar"
                    stroke-width="2.5"
                    stroke-linecap="round"
                />
                <path
                    :class="{ bar: true, middle: true, open: showMobileMenu }"
                    d="M12 20H28"
                    class="menu-bar"
                    stroke-width="2.5"
                    stroke-linecap="round"
                />
                <path
                    :class="{ bar: true, bottom: true, open: showMobileMenu }"
                    d="M12 26H28"
                    class="menu-bar"
                    stroke-width="2.5"
                    stroke-linecap="round"
                />
            </svg>
          </button>
        </div>
      </div>
  
      <!-- Profile menu -->
      <nav class="profile-nav" :class="{ open: isProfileMenuOpen }">
        <ul class="profile-links">
          <li>
            <router-link :to="'/stillingar'">{{ t('stillingar_header') }}</router-link>
          </li>
          <li>
            <button @click="logout">{{ t('header_utskraning') }}</button>
          </li>
          <li class="language-section">
            <div class="language-toggle">
              <button 
                @click="changeLang('isl')" 
                class="lang-option"
                :class="{ active: locale === 'isl' }"
              >
                <img :src="IcelandicFlag" alt="Íslenska" class="flag" />
                <span>Íslenska</span>
              </button>
              <button 
                @click="changeLang('en')" 
                class="lang-option"
                :class="{ active: locale === 'en' }"
              >
                <img :src="ENFlag" alt="English" class="flag" />
                <span>English</span>
              </button>
            </div>
          </li>
        </ul>
      </nav>

      <!-- Mobile nav links -->
      <nav class="mobile-nav" :class="{ open: showMobileMenu }">
          <ul class="nav-links drilldown" :class="{ sub: menuStack.length > 0 }">
            <div v-if="menuStack.length > 0" class="nav-back">
                <button @click="drillBack">← {{ menuStack[menuStack.length - 1].label }}</button>
            </div>
            <li v-for="link in currentMenu" :key="link.to">
              <template v-if="link.children.length">
                  <button class="nav-item" @click="drillInto(link)">
                  {{ link.label }}
                  <v-icon icon="mdi-chevron-right" class="drill-arrow" />
                  </button>
              </template>
              <template v-else>
                  <router-link :to="link.to">{{ link.label }}</router-link>
              </template>
            </li>
        </ul>
        </nav>
    </header>
  </template>
  

<style lang="scss" scoped>
@use "@/scss/settings/variables" as var;
@use "@/scss/settings/media-queries" as mq;

// Header styling
.mobile-header {
  background-color: var.$color-background;
  border-bottom: 1px solid var.$color-border;
  padding: var.$spacing-md var.$spacing-lg;


  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1000;
  }

  &__logo img {
    height: 40px;
    width: auto;
  }

  &__actions {
    display: flex;
    gap: var.$spacing-sm;
  }
}

// Menu toggle icon animation
.menu-icon .bar {
  transition: transform 0.3s ease, opacity 0.2s ease;
  transform-origin: center;
}

.menu-icon .top.open {
  transform: translate(-4px, 6px) rotate(45deg);
}

.menu-icon .middle.open {
  opacity: 0;
}

.menu-icon .bottom.open {
  transform: translate(-4px, -3px) rotate(-45deg);
}

// Icon colors
.icon-bg {
  fill: var.$color-surface;
}

.icon-path {
  fill: var.$color-text;
}

.menu-bar {
  stroke: var.$color-text;
}



// Fullscreen mobile nav
.mobile-nav {
  position: fixed;
  inset: 0;
  z-index: 999;
  background-color: var.$color-background;
  padding: var.$spacing-lg;
  display: none;
  margin-top: var.$spacing-xxl;


  &.open {
    display: block;
  }

  // Drillback navigation
  .nav-back {
    margin-bottom: var.$spacing-md;

    button {
      width: 100%;
      background-color: var.$color-surface;
      border: none;
      font: inherit;
      padding: var.$spacing-md;
      text-align: left;
      color: var.$color-primary;
      border-radius: var.$border-radius-md;
    }
  }

  // Main nav links (drilldown container)
  .nav-links {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var.$spacing-sm;


    &.drilldown {
      transition: transform 0.3s ease;
    }

    &.sub {
      animation: slide-left 0.3s forwards;
    }

    li {
      a,
      .nav-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var.$spacing-md;
        width: 100%;
        background: none;
        border: none;
        text-align: left;
        font: inherit;
        color: var.$color-text;
        font-weight: var.$font-weight-medium;
        text-decoration: none;
        border-bottom: 1px solid var.$color-border;
        font-size: var.$font-size-lg;

        &:hover {
          color: var.$color-primary;
          background-color: var.$color-surface;
        }
      }
    }
  }

  .drill-arrow {
    font-size: 18px;
  }

  @keyframes slide-left {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
}

// Profile menu styling (fullscreen like mobile nav)
.profile-nav {
  position: fixed;
  inset: 0;
  z-index: 999;
  background-color: var.$color-background;
  padding: var.$spacing-lg;
  display: none;
  margin-top: var.$spacing-xxl;

  &.open {
    display: flex;
    flex-direction: column;
  }

  .profile-links {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var.$spacing-sm;
    flex: 1;

    li {
      a,
      button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var.$spacing-md;
        width: 100%;
        background: none;
        border: none;
        text-align: left;
        font: inherit;
        color: var.$color-text;
        font-weight: var.$font-weight-medium;
        text-decoration: none;
        border-bottom: 1px solid var.$color-border;
        font-size: var.$font-size-lg;

        &:hover {
          color: var.$color-primary;
          background-color: var.$color-surface;
        }
      }

      &.language-section {
        border-bottom: none;
        border-top: 1px solid var.$color-border;
        padding: var.$spacing-md;
        margin-top: auto;
      }
    }
  }

  .language-toggle {
    display: flex;
    gap: var.$spacing-sm;
    width: 100%;

    .lang-option {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: var.$spacing-sm var.$spacing-md;
      background-color: transparent;
      border: 1px solid var.$color-border;
      border-radius: var.$border-radius-md;
      cursor: pointer;
      transition: all 0.2s ease;
      font-weight: var.$font-weight-medium;
      color: var.$color-text;

      .flag {
        width: 20px;
        height: auto;
      }

      &.active {
        background-color: var.$color-surface;
        border-color: var.$color-border;
        color: var.$color-text;
      }

      &:hover:not(.active) {
        background-color: var.$color-background;
      }
    }
  }
}

// Dark mode support
body.dark-mode {
  .mobile-header {
    background-color: var.$color-background-dark;
    border-bottom-color: var.$color-border-dark;
  }

  .mobile-nav {
    background-color: var.$color-background-dark;

    .nav-back button {
      background-color: var.$color-surface-dark;
      color: var.$color-primary-dark;
    }

    .nav-links li {
      a,
      .nav-item {
        color: var.$color-text-dark;
        border-bottom-color: var.$color-border-dark;

        &:hover {
          color: var.$color-primary-dark;
          background-color: var.$color-surface-dark;
        }
      }
    }
  }

  .profile-nav {
    background-color: var.$color-background-dark;

    .profile-links li {
      a,
      button {
        color: var.$color-text-dark;
        border-bottom-color: var.$color-border-dark;

        &:hover {
          color: var.$color-primary-dark;
          background-color: var.$color-surface-dark;
        }
      }

      &.language-section {
        border-bottom-color: var.$color-border-dark;
      }
    }

    .language-toggle {
      .lang-option {
        border-color: var.$color-border-dark;
        color: var.$color-text-dark;

        &.active {
          background-color: var.$color-surface-dark;
          border-color: var.$color-border-dark;
          color: var.$color-text-dark;
        }

        &:hover:not(.active) {
          background-color: var.$color-background-dark;
        }
      }
    }
  }

  // SVG icon colors
  .icon-bg {
    fill: var.$color-surface-dark;
  }

  .icon-path {
    fill: var.$color-text-dark;
  }

  .menu-bar {
    stroke: var.$color-text-dark;
  }
}

// Profile menu styling
.profile-menu {
  min-width: 200px;
  padding: 0;
}

.profile-menu-item {
  padding: var.$spacing-md var.$spacing-lg;
  font-size: var.$font-size-lg;
  font-weight: var.$font-weight-medium;
  border-bottom: 1px solid var.$color-border;

  &:last-child {
    border-bottom: none;
  }

  :deep(.v-list-item-title) {
    font-size: var.$font-size-lg;
    font-weight: var.$font-weight-medium;
  }
}

body.dark-mode {
  .profile-menu-item {
    border-bottom-color: var.$color-border-dark;
  }
}
</style>